import { useState } from 'react';
import api from '../../../services/api';
import logger from '../../../utils/logging/logger';

// handleProceedToPayment for useDonationForm, extracted VERBATIM: creates the
// one-time or monthly (subscription/subscription-schedule) payment intent
// when the user clicks "Proceed to Payment".
export function useProceedToPayment({
  donationType,
  oneTimeAmount,
  setOneTimeAmount,
  preservedOneTimeAmount,
  setPreservedOneTimeAmount,
  monthlyAmount,
  setMonthlyAmount,
  preservedMonthlyAmount,
  setPreservedMonthlyAmount,
  isForever,
  preservedIsForever,
  setPreservedIsForever,
  numberOfPayments,
  preservedNumberOfPayments,
  setPreservedNumberOfPayments,
  firstName,
  lastName,
  email,
  phone,
  donationFor,
  user,
  setError,
  onClientSecretChange,
  setClientSecret,
  setPaymentIntentId,
  setPreservedDonationType,
  paymentIntentCreatedRef,
  setShowPaymentForm,
  setShowPaymentElement,
  setSubscriptionData,
  setPreservedSubscriptionDataFromParent,
}) {
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);

  const handleProceedToPayment = async () => {
    if (creatingPaymentIntent || paymentIntentCreatedRef.current) {
      return; // Already creating or created
    }

    setCreatingPaymentIntent(true);
    setError('');

    try {
      logger.info('User clicked Proceed to Payment - creating donation payment intent');

      if (donationType === 'one-time') {
        // Create one-time donation payment intent
        const currentAmount = oneTimeAmount ? parseFloat(oneTimeAmount) : (preservedOneTimeAmount || 0);

        if (!currentAmount || currentAmount < 0.01 || isNaN(currentAmount)) {
          setError('Please enter a valid donation amount.');
          setCreatingPaymentIntent(false);
          return;
        }

        // Format the displayed value to xx.xx
        setOneTimeAmount(currentAmount.toFixed(2));

        const requestBody = {
          oneTimeAmount: currentAmount,
          donationType: 'one-time',
        };

        if (firstName && firstName.trim()) {
          requestBody.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          requestBody.lastName = lastName.trim();
        }
        if (email && email.trim()) {
          requestBody.email = email.trim();
        }
        if (phone && phone.trim()) {
          requestBody.phone = phone.trim();
        }
        if (!user && donationFor && donationFor.trim()) {
          requestBody.donationFor = donationFor.trim();
        }

        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(requestBody)
        });

        if (response.success && response.oneTimePayment) {
          setPreservedOneTimeAmount(currentAmount);
          const newClientSecret = response.oneTimePayment.clientSecret;

          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }

          setClientSecret(newClientSecret);
          setPaymentIntentId(response.oneTimePayment.paymentIntentId);
          setPreservedDonationType('one-time');
          paymentIntentCreatedRef.current = true;

          // Wait a moment for clientSecret to be set
          await new Promise(resolve => setTimeout(resolve, 200));

          if (newClientSecret) {
            setShowPaymentForm(true);
            setShowPaymentElement(true);
            logger.info('One-time donation payment intent created, showing payment form');
          } else {
            setError('Failed to initialize payment. Please try again.');
            logger.error('Payment intent not created after Proceed to Payment click');
          }
        } else {
          setError(response.message || response.error || 'Failed to create payment intent. Please try again.');
          logger.error('Failed to create one-time donation payment intent', { response });
        }
      } else if (donationType === 'monthly') {
        // Create monthly donation payment intent/subscription
        const currentMonthlyAmount = preservedMonthlyAmount || parseFloat(monthlyAmount);
        const currentIsForever = preservedIsForever || isForever;
        const currentNumberOfPayments = preservedNumberOfPayments || numberOfPayments;

        if (!currentMonthlyAmount || currentMonthlyAmount < 0.01 || isNaN(currentMonthlyAmount)) {
          setError('Please enter a valid monthly donation amount.');
          setCreatingPaymentIntent(false);
          return;
        }

        // Format the displayed value to xx.xx
        setMonthlyAmount(currentMonthlyAmount.toFixed(2));

        if (!currentIsForever && (!currentNumberOfPayments || currentNumberOfPayments < 1 || currentNumberOfPayments > 120)) {
          setError('Please select a valid number of payments (1-120) or select Unlimited.');
          setCreatingPaymentIntent(false);
          return;
        }

        const requestBody = {
          monthlyAmount: currentMonthlyAmount,
          numberOfPayments: currentIsForever ? 'forever' : currentNumberOfPayments,
          donationType: 'monthly',
        };

        if (firstName && firstName.trim()) {
          requestBody.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          requestBody.lastName = lastName.trim();
        }
        if (email && email.trim()) {
          requestBody.email = email.trim();
        }
        if (phone && phone.trim()) {
          requestBody.phone = phone.trim();
        }
        if (!user && donationFor && donationFor.trim()) {
          requestBody.donationFor = donationFor.trim();
        }

        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(requestBody)
        });

        const responseClientSecret = response?.subscription?.clientSecret || response?.subscriptionSchedule?.clientSecret || response?.clientSecret;
        const responseSubscriptionData = response?.subscription || response?.subscriptionSchedule;

        if (response.success && responseClientSecret) {
          setPreservedMonthlyAmount(currentMonthlyAmount);
          setPreservedIsForever(currentIsForever);
          setPreservedNumberOfPayments(currentIsForever ? 'forever' : currentNumberOfPayments);
          const newClientSecret = responseClientSecret;

          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }

          setClientSecret(newClientSecret);
          setSubscriptionData(responseSubscriptionData || { clientSecret: newClientSecret });
          setPreservedSubscriptionDataFromParent(responseSubscriptionData || { clientSecret: newClientSecret });
          setPreservedDonationType('monthly');
          paymentIntentCreatedRef.current = true;

          // Wait a moment for clientSecret to be set
          await new Promise(resolve => setTimeout(resolve, 200));

          if (newClientSecret) {
            setShowPaymentForm(true);
            setShowPaymentElement(true);
            logger.info('Monthly donation payment intent created, showing payment form');
          } else {
            setError('Failed to initialize payment. Please try again.');
            logger.error('Payment intent not created after Proceed to Payment click');
          }
        } else {
          setError(response.message || response.error || 'Failed to create payment intent. Please try again.');
          logger.error('Failed to create monthly donation payment intent', { response });
        }
      }
    } catch (error) {
      logger.error('Error creating donation payment intent on Proceed to Payment', { error });
      setError('Failed to initialize payment. Please try again.');
    } finally {
      setCreatingPaymentIntent(false);
    }
  };

  return { creatingPaymentIntent, handleProceedToPayment };
}
